/**
 * Serbian relative time, for the "last drawn by" line and everything after it.
 *
 * `Intl.RelativeTimeFormat` with `sr` gets the grammar right but not the
 * register — it produces "pre 1 minut" and "pre 1 sat", which nobody says. The
 * cost of doing it by hand is one plural rule and four special cases.
 */
/**
 * Serbian has three plural forms, chosen by the last digit with an exception
 * for the teens: 21 takes the same form as 1, but 11 does not.
 */
export function pluralSr(n, one, few, many) {
    const abs = Math.abs(n);
    const last = abs % 10;
    const teens = abs % 100;
    if (last === 1 && teens !== 11)
        return one;
    if (last >= 2 && last <= 4 && (teens < 12 || teens > 14))
        return few;
    return many;
}
const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
/**
 * Past-tense only, which is all this codebase needs — nothing here is drawn in
 * the future. Anything older than a week becomes a plain date, because "pre 43
 * dana" is a worse answer than the date it happened.
 */
export function relativeTimeSr(when, now = new Date()) {
    const then = typeof when === 'string' ? new Date(when) : when;
    if (Number.isNaN(then.getTime()))
        return '';
    const ms = now.getTime() - then.getTime();
    // Clock skew between a client and the server is normal and small; treating a
    // few seconds in the future as "just now" beats rendering a negative age.
    if (ms < 45 * 1000)
        return 'upravo sad';
    if (ms < 90 * 1000)
        return 'pre minut';
    if (ms < HOUR) {
        const n = Math.round(ms / MIN);
        return `pre ${n} ${pluralSr(n, 'minut', 'minuta', 'minuta')}`;
    }
    if (ms < 90 * MIN)
        return 'pre sat vremena';
    if (ms < DAY) {
        const n = Math.round(ms / HOUR);
        return `pre ${n} ${pluralSr(n, 'sat', 'sata', 'sati')}`;
    }
    if (ms < 2 * DAY)
        return 'juče';
    if (ms < 7 * DAY) {
        const n = Math.round(ms / DAY);
        return `pre ${n} ${pluralSr(n, 'dan', 'dana', 'dana')}`;
    }
    return `${then.getDate()}.${then.getMonth() + 1}.${then.getFullYear()}.`;
}
// ---------------------------------------------------------------------------
// Showtimes
// ---------------------------------------------------------------------------
/**
 * A screening's date and time, in parts.
 *
 * Parts rather than one formatted string because DESIGN.md §7 is blunt about
 * this surface: *the date and time are never stylized past legibility*. The
 * card wants the weekday and the hour big and the rest small, and it can only
 * do that if it gets them separately. A single string would force the card to
 * either split it back apart or give up and render one size.
 *
 * `Intl.DateTimeFormat('sr')` is not used, for the same reason `relativeTimeSr`
 * does not use `RelativeTimeFormat`: it produces "чет" in Cyrillic, and
 * decision #1 is Latin. Two arrays are cheaper than transliterating output.
 */
/** Indexed by `Date#getDay()`, so Sunday first. */
export const WEEKDAYS_SR = [
    'nedelja',
    'ponedeljak',
    'utorak',
    'sreda',
    'četvrtak',
    'petak',
    'subota',
];
export const WEEKDAYS_SR_SHORT = ['ned', 'pon', 'uto', 'sre', 'čet', 'pet', 'sub'];
/** Genitive: "12. septembra", which is how a date is said, not "septembar". */
export const MONTHS_SR_GENITIVE = [
    'januara',
    'februara',
    'marta',
    'aprila',
    'maja',
    'juna',
    'jula',
    'avgusta',
    'septembra',
    'oktobra',
    'novembra',
    'decembra',
];
const pad = (n) => (n < 10 ? `0${n}` : String(n));
/** Local calendar day, as a number that compares. Not UTC — 23:30 on Thursday
 * in Belgrade is Friday in UTC, and the card must say Thursday. */
const dayIndex = (d) => Math.floor((d.getTime() - d.getTimezoneOffset() * 60_000) / 86_400_000);
export function formatShowtime(when, now = new Date()) {
    const at = typeof when === 'string' ? new Date(when) : when;
    const days = dayIndex(at) - dayIndex(now);
    const isToday = days === 0;
    const isTomorrow = days === 1;
    const weekday = WEEKDAYS_SR[at.getDay()] ?? '';
    const weekdayShort = WEEKDAYS_SR_SHORT[at.getDay()] ?? '';
    return {
        weekday,
        weekdayShort,
        dayMonth: `${at.getDate()}. ${MONTHS_SR_GENITIVE[at.getMonth()] ?? ''}`,
        time: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
        headline: isToday ? 'danas' : isTomorrow ? 'sutra' : weekday,
        isToday,
        isTomorrow,
        isPast: at.getTime() < now.getTime(),
    };
}
/**
 * "za 3 dana", "za 2 sata", "za 20 minuta" — the countdown line under the
 * headline. Future-tense only; `relativeTimeSr` above covers the past, and a
 * screening that has started says so in words rather than in negative minutes.
 */
export function untilShowtimeSr(when, now = new Date()) {
    const at = typeof when === 'string' ? new Date(when) : when;
    const ms = at.getTime() - now.getTime();
    if (Number.isNaN(ms))
        return '';
    if (ms <= 0)
        return 'u toku';
    const minutes = Math.round(ms / 60_000);
    if (minutes < 60) {
        return `za ${minutes} ${pluralSr(minutes, 'minut', 'minuta', 'minuta')}`;
    }
    const hours = Math.round(ms / 3_600_000);
    if (hours < 24)
        return `za ${hours} ${pluralSr(hours, 'sat', 'sata', 'sati')}`;
    const days = Math.round(ms / 86_400_000);
    if (days <= 14)
        return `za ${days} ${pluralSr(days, 'dan', 'dana', 'dana')}`;
    const weeks = Math.round(days / 7);
    return `za ${weeks} ${pluralSr(weeks, 'nedelju', 'nedelje', 'nedelja')}`;
}
/**
 * "3 od 7 slobodno", exactly as DESIGN.md §7 writes it — `slobodno` is adverbial
 * here and does not decline, which is why this one line needs no plural rule.
 *
 * Returns null when the room has no seat map yet, which is every room until the
 * seat editor lands: a card must not claim "0 od 0 slobodno".
 */
export function seatsFreeSr(free, total) {
    if (total <= 0)
        return null;
    if (free <= 0)
        return 'popunjeno';
    return `${free} od ${total} slobodno`;
}
//# sourceMappingURL=time.js.map